/**
 * Mojo Up AI Cloud → Team → Billing: the plan, the seats used of those bought, and the way to change
 * them. Mojo Up billing (the customer portal) sells Teams; every button here ends at Stripe's own
 * pages, through the API. Owners and admins only.
 */

import { CLOUD_TRIAL_DAYS, type CloudBilling, type CloudSubscriptionStatus } from '@aioe/shared';
import { dateLong, dateMedium } from '@aioe/shared/wording/dateTime';
import { CreditCard, TriangleExclamation } from '@gravity-ui/icons';
import { Button, Input, Label, TextField } from '@heroui/react';
import { useEffect, useState } from 'react';

import { problemText, routes } from '../../api/client.ts';
import { ArtEmpty, ListBlock, ListGroup, ListRow, StatusMark, type ArtTone } from '../ui.tsx';
import { FormModal } from './FormModal.tsx';

const longDay = (iso: string) => dateLong(iso);
const dollars = (cents: number) => `A$${(cents / 100).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

type MarkState = Parameters<typeof StatusMark>[0]['state'];
/** Every status as a dot and its words: colour is never the only signal. */
export function billingStatus(b: Pick<CloudBilling, 'status' | 'trialDaysLeft' | 'cancelAtPeriodEnd'> & Partial<Pick<CloudBilling, 'source' | 'plan' | 'licence'>>): { state: MarkState; label: string } {
  if (b.source === 'licence') {
    if (b.plan === 'teams' && b.licence) return { state: 'connected', label: `Licensed until ${dateMedium(b.licence.until)}` };
    return { state: 'disabled', label: b.status === 'canceled' ? 'Licence revoked' : 'Licence ended' };
  }
  const s: Record<CloudSubscriptionStatus, { state: MarkState; label: string }> = {
    trialing: { state: 'online', label: b.trialDaysLeft !== undefined ? `Free trial, ${plural(b.trialDaysLeft, 'day')} left` : 'Free trial' },
    active: { state: 'connected', label: b.cancelAtPeriodEnd ? 'Active, ending' : 'Active' },
    past_due: { state: 'medium', label: 'Payment overdue' },
    paused: { state: 'error', label: 'Paused, no payment method' },
    unpaid: { state: 'error', label: 'Unpaid' },
    canceled: { state: 'disabled', label: 'Cancelled' },
    incomplete: { state: 'medium', label: 'Payment needed' },
    incomplete_expired: { state: 'disabled', label: 'Not completed' },
    none: { state: 'idle', label: 'Free plan' },
  };
  return s[b.status];
}

/** Sends the browser to Stripe: Checkout or the customer portal. */
const go = (url: string) => window.location.assign(url);

/** `owner`: only a team's owners change its billing (the API refuses an admin), so only they get the button. */
export function BillingBanner({ billing, owner, onManage }: { billing: CloudBilling; owner: boolean; onManage: () => void }) {
  if (!billing.banner) return null;
  const danger = billing.banner.tone === 'danger';
  return (
    <div role="status" className={`flex flex-wrap items-center gap-3 rounded-xl border px-4 py-3 text-sm ${danger ? 'border-danger/40 bg-danger/10' : 'border-warning/40 bg-warning/10'}`}>
      <TriangleExclamation className={`size-4 shrink-0 ${danger ? 'text-danger' : 'text-warning'}`} aria-hidden="true" />
      <span className="min-w-[14rem] flex-1">{billing.banner.text}</span>
      {billing.hasBillingAccount && billing.configured ? (
        owner ? (
          <Button size="sm" variant="secondary" onPress={onManage}>
            Manage billing
          </Button>
        ) : (
          <span className="text-xs text-muted">One of the team’s owners can sort this out.</span>
        )
      ) : null}
    </div>
  );
}

/** `owner`: the actions are the owners' alone (the API refuses an admin); an admin reads the figures. */
export function BillingSection({ tone, organisationId, billing, owner, onAddSeats, onStartTrial }: { tone: ArtTone; organisationId: string; billing: CloudBilling; owner: boolean; onAddSeats: () => void; onStartTrial: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const manage = async () => {
    setBusy(true);
    setError(undefined);
    try {
      go((await routes.billingPortal(organisationId, billing.status === 'paused' || billing.status === 'unpaid' ? 'payment_method_update' : undefined)).url);
    } catch (e) {
      setError(problemText(e));
      setBusy(false);
    }
  };
  if (!billing.configured) {
    return (
      <ListGroup
        title="Billing"
        empty={
          <ArtEmpty tone={tone} icon={<CreditCard />} title="Billing is not set up here" compact>
            Teams cannot be bought online here, so teams are not limited by seats.
          </ArtEmpty>
        }
      />
    );
  }
  const live = billing.status === 'trialing' || billing.status === 'active' || billing.status === 'past_due';
  const needsCard = billing.status === 'paused' || billing.status === 'unpaid' || billing.status === 'incomplete';
  const used = billing.members + billing.openInvitations;
  const status = billingStatus(billing);
  const date: { label: string; value: string } =
    billing.status === 'trialing' && billing.trialEnd
      ? { label: 'Trial ends', value: longDay(billing.trialEnd) }
      : live && billing.periodEnd
        ? { label: billing.cancelAtPeriodEnd ? 'Ends' : 'Renews', value: longDay(billing.periodEnd) }
        : { label: 'Trial', value: billing.canStartTrial ? `${CLOUD_TRIAL_DAYS} days free, no card` : 'Used' };
  // One primary action, and at most two beside it; none for an admin.
  const actions = !owner ? undefined : live ? (
    <>
      <Button size="sm" variant="primary" onPress={onAddSeats} isDisabled={busy}>
        Add seats
      </Button>
      <Button size="sm" variant="secondary" onPress={() => void manage()} isDisabled={busy}>
        Manage billing
      </Button>
    </>
  ) : needsCard ? (
    <Button size="sm" variant="primary" onPress={() => void manage()} isDisabled={busy}>
      {billing.status === 'incomplete' ? 'Finish payment' : 'Add a payment method'}
    </Button>
  ) : (
    <>
      <Button size="sm" variant="primary" onPress={onStartTrial} isDisabled={busy}>
        {billing.canStartTrial ? 'Start free trial' : 'Upgrade to Teams'}
      </Button>
      {billing.hasBillingAccount ? (
        <Button size="sm" variant="secondary" onPress={() => void manage()} isDisabled={busy}>
          Invoices
        </Button>
      ) : null}
    </>
  );
  return (
    <ListGroup title="Billing" description={`${billing.priceText}.`} actions={actions}>
      <ListRow icon={<CreditCard />} title={billing.plan === 'teams' ? 'Teams' : 'Free'} description="The plan" {...(billing.status === 'none' ? { value: 'No subscription yet' } : { status: <StatusMark state={status.state} label={status.label} /> })} />
      <ListRow
        title="Seats"
        description={`${plural(billing.members, 'person', 'people')}${billing.openInvitations ? `, ${plural(billing.openInvitations, 'open invitation')}` : ''}${billing.plan === 'free' ? '. The free plan holds only the owner.' : ''}`}
        value={<span className="tabular-nums text-foreground">{`${used} of ${billing.seatLimit} used`}</span>}
      />
      <ListRow title={date.label} {...(billing.stale ? { description: 'Billing has not answered lately; showing its last word' } : {})} value={<span className="text-foreground">{date.value}</span>} />
      {error ? (
        <ListBlock>
          <p role="alert" className="m-0 text-sm text-danger">
            {error}
          </p>
        </ListBlock>
      ) : null}
      <SeatsHint billing={billing} />
      {owner ? null : <ListBlock className="text-xs text-muted">The team’s owners change its plan, seats and billing details.</ListBlock>}
    </ListGroup>
  );
}

function SeatsHint({ billing }: { billing: CloudBilling }) {
  const over = billing.members - billing.seatLimit;
  if (over <= 0) return null;
  return <ListBlock className="text-xs text-muted">This team has {plural(over, 'more person', 'more people')} than its plan allows. Everyone keeps their access; nobody new can join until there is a seat.</ListBlock>;
}

/** Start a free trial (once per team) or buy Teams: seats, then Stripe Checkout. */
export function TrialModal({ isOpen, onClose, organisationId, billing }: { isOpen: boolean; onClose: () => void; organisationId: string; billing: CloudBilling }) {
  const minimum = Math.max(1, billing.members + billing.openInvitations);
  const [seats, setSeats] = useState(String(Math.max(minimum, 2)));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const n = Number(seats);
  const valid = Number.isInteger(n) && n >= minimum && n <= 500;
  const start = async () => {
    setBusy(true);
    setError(undefined);
    try {
      go((await routes.startCheckout(organisationId, n)).url);
    } catch (e) {
      setError(problemText(e));
      setBusy(false);
    }
  };
  const trial = billing.canStartTrial;
  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title={trial ? 'Start a free trial of Teams' : 'Upgrade to Teams'}
      blurb={
        trial
          ? `${CLOUD_TRIAL_DAYS} days free with no card. After that, ${billing.priceText}. Stripe asks for your organisation's legal name and ABN on the next page.`
          : `${billing.priceText}. Stripe asks for a card, your organisation's legal name and ABN on the next page.`
      }
      submitLabel={trial ? 'Continue to Stripe' : 'Continue to payment'}
      canSubmit={valid}
      busy={busy}
      error={error}
      onSubmit={() => void start()}
    >
      <TextField variant="secondary" value={seats} onChange={setSeats} isRequired autoFocus>
        <Label>Seats</Label>
        <Input type="number" inputMode="numeric" min={minimum} max={500} className="max-w-32 tabular-nums" />
      </TextField>
      <p className="text-xs text-muted">One for each person in the team, and each open invitation: at least {minimum}. You can add seats at any time.</p>
    </FormModal>
  );
}

/** Change the number of seats: more are charged now, prorated; fewer lower the bill from the next renewal. */
export function SeatsModal({ isOpen, onClose, organisationId, billing, onChanged }: { isOpen: boolean; onClose: () => void; organisationId: string; billing: CloudBilling; onChanged: () => void }) {
  const held = billing.members + billing.openInvitations;
  const [seats, setSeats] = useState(String(billing.seatsPurchased + 1));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  // The preview answer, kept with the number it was for, so a stale one is never shown.
  const [preview, setPreview] = useState<{ seats: number; amountDue?: number }>();
  const n = Number(seats);
  const valid = Number.isInteger(n) && n >= Math.max(1, held) && n <= 500 && n !== billing.seatsPurchased;
  const adding = valid && n > billing.seatsPurchased;
  // What adding would charge today, asked of Stripe through the portal; nothing changes until Save.
  useEffect(() => {
    if (!isOpen || !adding || billing.status === 'trialing') return;
    const t = setTimeout(() => {
      routes
        .changeSeats(organisationId, n, true)
        .then((r) => setPreview({ seats: n, ...(r.prorationPreview ? { amountDue: r.prorationPreview.amountDue } : {}) }))
        .catch(() => setPreview(undefined));
    }, 400);
    return () => clearTimeout(t);
  }, [isOpen, adding, n, organisationId, billing.status]);
  const charge = preview?.seats === n ? preview.amountDue : undefined;
  const save = async () => {
    setBusy(true);
    setError(undefined);
    try {
      await routes.changeSeats(organisationId, n);
      onChanged();
      onClose();
    } catch (e) {
      setError(problemText(e));
    } finally {
      setBusy(false);
    }
  };
  const note = !valid
    ? n < held
      ? `This team holds ${plural(held, 'seat')} (people and open invitations), so it needs at least ${held}.`
      : `The team has ${plural(billing.seatsPurchased, 'seat')} now.`
    : adding
      ? billing.status === 'trialing'
        ? 'Nothing is charged during the free trial.'
        : charge !== undefined
          ? `${dollars(charge)} is charged now for the rest of this month, including GST.`
          : 'The extra seats are charged now, for the rest of this month.'
      : 'The lower number is billed from the next renewal; this month is not refunded.';
  return (
    <FormModal isOpen={isOpen} onClose={onClose} title="Change seats" blurb={`${billing.priceText}. Each person and each open invitation holds a seat.`} submitLabel="Save seats" canSubmit={valid} busy={busy} error={error} onSubmit={() => void save()}>
      <TextField variant="secondary" value={seats} onChange={setSeats} isRequired autoFocus>
        <Label>Seats</Label>
        <Input type="number" inputMode="numeric" min={Math.max(1, held)} max={500} className="max-w-32 tabular-nums" />
      </TextField>
      <p className="text-xs text-muted">{note}</p>
    </FormModal>
  );
}

/** A licence code Mojo Up issued: typed once by an owner, bound to this team, and the plan follows at once. */
export function RedeemModal({ isOpen, onClose, organisationId, onRedeemed }: { isOpen: boolean; onClose: () => void; organisationId: string; onRedeemed: () => void }) {
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [done, setDone] = useState<CloudBilling>();
  const looksRight = /^mjcl_[a-z0-9]{32}$/.test(code.replace(/[\s-]+/g, '').toLowerCase());
  const redeem = async () => {
    setBusy(true);
    setError(undefined);
    try {
      const b = await routes.redeemLicence(organisationId, code);
      setDone(b);
      onRedeemed();
    } catch (e) {
      setError(problemText(e));
    } finally {
      setBusy(false);
    }
  };
  if (done) {
    return (
      <FormModal
        isOpen={isOpen}
        onClose={onClose}
        title="Licence redeemed"
        blurb={done.licence ? `${plural(done.licence.seats, 'seat')} on Teams until ${longDay(done.licence.until)}, with nothing charged. Invite people from the Team page.` : 'The team is on Teams now.'}
        submitLabel="Done"
        onSubmit={onClose}
        footer={
          <Button variant="primary" onPress={onClose}>
            Done
          </Button>
        }
      />
    );
  }
  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title="Redeem a licence"
      blurb="A licence code puts the team on Teams for the seats and time it names, with no subscription. A code works once, for one team."
      submitLabel="Redeem"
      canSubmit={looksRight}
      busy={busy}
      error={error}
      onSubmit={() => void redeem()}
    >
      <TextField variant="secondary" value={code} onChange={setCode} isRequired autoFocus>
        <Label>Licence code</Label>
        <Input placeholder="mjcl_…" autoComplete="off" spellCheck={false} className="font-mono" />
      </TextField>
      <p className="text-xs text-muted">{code && !looksRight ? 'A code starts with mjcl_ and has 32 letters and digits after it; spaces and dashes do not matter.' : 'Paste it as you received it.'}</p>
    </FormModal>
  );
}
